import axios from "axios";
import { JSON_CONTENT_FORM_CONFIG } from "../Config";
import { getJSONContentFormConfig } from "../Config";

/**
 * Fetches a list of invoices.
 *
 * @param {number} page - The page number to retrieve.
 * @param {number} perPage - Number of results per page.
 * @param {?number} orderId - Optional order ID to filter.
 * @param {number[]} branch - Array of branch IDs.
 * @returns {Promise<Object>} Resolves to the response data from the API.
 *                            On success, returns an object with:
 *                            - `count` (number): total number of invoices,
 *                            - `next` (string|null): URL to next page, if available,
 *                            - `previous` (string|null): URL to previous page, if available,
 *                            - `results` (Array): array of invoice objects,
 *                            - `num_pages` (number): total number of pages.
 *                            On failure (e.g., permission denied), returns:
 *                            `{ success: false, detail: "You do not have permission to view invoices." }`.
 */
export const listInvoices = async (
  page = 1,
  perPage = 18,
  orderId = null,
  branch = []
) => {
  let url = new URL(
    `${process.env.REACT_APP_API_BASE_URL}/invoices/get-invoices/`
  );
  url.searchParams.append("page", page);
  url.searchParams.append("page_size", perPage);
  if (orderId) {
    url.searchParams.append("order", orderId);
  }
  if (Array.isArray(branch) && branch.length > 0) {
    url.searchParams.append("branch", branch.join(","));
  }
  try {
    const res = await axios.get(url, JSON_CONTENT_FORM_CONFIG);
    return res.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      return {
        success: false,
        detail: error.response.data.detail,
      };
    }
    return {
      success: false,
      detail: "An unexpected error occurred while fetching invoices.",
    };
  }
};

/**
 * Fetch details of an invoice.
 *
 * @param {number} invoiceId - invoice ID.
 * @returns {Promise<Object>} Resolves to the response data from the API.
 *                            On success, returns an invoice object
 *                            On failure (e.g., permission denied), returns:
 *                            `{ success: false, detail: "You do not have permission to view invoices." }`.
 */
export const getInvoiceDetail = async (invoiceId) => {
  try {
    let url = new URL(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/get-invoice/${invoiceId}/`
    );
    const res = await axios.get(url, JSON_CONTENT_FORM_CONFIG);
    return res.data;
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      return {
        success: false,
        detail: error.response.data.detail,
      };
    }
    return {
      success: false,
      detail:
        "Error while fetching: You may not have permission to view this invoice.",
    };
  }
};

/**
 * Creates an invoice from the order.
 *
 * @param {number} orderId - order ID for which invoice to be generated.
 * @returns {Promise<Object>} Resolves to the response data from the API.
 *                            On success, returns an invoice object
 *                            On failure (e.g., permission denied), returns:
 *                            `{ success: false, detail: "You do not have permission to view invoices." }`.
 */
export const createInvoice = async (order) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/create-invoice/`,
      JSON.stringify({ order: order }),
      getJSONContentFormConfig()
    );
    return { success: true, invoice: res.data };
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      return {
        success: false,
        detail: error.response.data.detail,
      };
    }
    return {
      success: false,
      detail: "An unexpected error occurred while creating the invoice.",
    };
  }
};

/**
 * Post an invoice.
 *
 * @param {number} invoiceId - draft or proforma invoice ID for which invoice to be posted.
 * @returns {Promise<object>} Resolves to the response data from the API.
 *                            On success, returns the generated invoice no
 *                            On failure (e.g., permission denied), returns:
 *                            `{ success: false, detail: "You do not have permission to view invoices." }`.
 */
export const postInvoice = async (invoiceId) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/post-invoice/${invoiceId}/`,
      {},
      getJSONContentFormConfig()
    );
    return { success: true, invoiceNo: res.data.invoice_no };
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      return {
        success: false,
        detail: error.response.data.detail,
      };
    }
    return {
      success: false,
      detail: "An unexpected error occurred while posting the invoice.",
    };
  }
};

/**
 * Update Invoice Info.
 *
 * * @param {number} invoiceId - invoice ID for which info to be updated.
 * @param {object} data - object in the form `{eway_bill_no: xyz, vehicle_no: abc}`.
 * @returns {Promise<Object>} Resolves to the response data from the API.
 *                            On success, returns {success: true}
 *                            On failure (e.g., permission denied), returns:
 *                            `{ success: false, detail: "You do not have permission to view invoices." }`.
 */
export const updateInvoiceInfo = async (invoiceId, data) => {
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/invoices/update-invoice-information/${invoiceId}/`,
      JSON.stringify(data),
      getJSONContentFormConfig()
    );
    return { success: true };
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      return {
        success: false,
        detail: error.response.data.detail,
      };
    }
    return {
      success: false,
      detail: "An unexpected error occurred while creating the invoice.",
    };
  }
};
